import HuskyTooltip from "@husky-hrc/Components/FormFields/HuskyTooltip";
import InputSubText from "@husky-hrc/Components/FormFields/InputSubText";
import RevertButton from "@husky-hrc/Components/FormFields/Revert";
import { InputModel } from "@husky-hrc/Framework/Types/ApiModel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import styled, { css } from "styled-components";

export interface ToggleFieldStyles {
    infoTextStyles?: TypographyPresentationProps;
    checkIconStyles?: IconPresentationProps;
    container?: InjectableCss;
    labelContainer?: InjectableCss;
    button?: ButtonPresentationProps;
    value?: TypographyPresentationProps;
}

export const toggleFieldStyles: ToggleFieldStyles = {
    infoTextStyles: {
        css: css`
            font-size: 16px;
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    checkIconStyles: {
        src: Check,
        size: 15,
        color: "danger.contrast",
        css: css`
            margin-right: 5px;
        `,
    },
    container: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    labelContainer: {
        css: css`
            margin-bottom: 10px;
            font-size: 20px;
            font-weight: 600;
            letter-spacing: 0.57px;
            line-height: 24px;
            display: flex;
            align-items: center;
        `,
    },
    button: {
        size: 50,
        variant: "secondary",
        css: css`
            display: flex;
            align-items: stretch;
            border: none;
            padding: 0;
            margin-top: 10px;
            min-height: 48px;

            &:hover,
            &:focus {
                outline: none;
                box-shadow: 0 0 0 3px rgba(116, 164, 248, 0.2);
                border: none;
            }
            &:focus {
                > div {
                    border: 1px solid ${props => getProp("theme.focus.color")};
                }
            }
        `,
    },
    value: {
        size: 16,
        weight: "bold",
        lineHeight: "28px",
    },
};

const ValueContainer = styled.div<{ selected: boolean }>`
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${props => (props.selected ? getColor("secondary.main") : getColor("common.background"))};
    transition: background-color 1s ease;
    border: 1px solid ${getColor("secondary.main")};

    > span,
    svg,
    & {
        color: ${props => (props.selected ? getColor("common.background") : getColor("secondary.main"))} !important;
    }
`;

interface OwnProps {
    input: InputModel;
    onChange: (parameter: string, value: string) => void;
    onUncommit: (parameter: string) => void;
}

type Props = OwnProps;

const styles = toggleFieldStyles;

const ToggleField = ({ input, onChange, onUncommit }: Props) => {
    const [value, setValue] = React.useState(input.value);

    React.useEffect(() => {
        setValue(input.value);
    }, [input]);

    const handleClick = () => {
        const parameter = input.name;
        let updatedValue;
        const updatedValueModel = input.values.find(val => val.description !== value);
        if (updatedValueModel) {
            updatedValue = updatedValueModel.name;
        }
        const defaultValue = input.defaultValue.name;

        if ((updatedValue !== defaultValue || !input.committed) && parameter && updatedValue) {
            onChange(parameter, updatedValue);
        }
    };

    const truthyValues = ["Yes", "optionOrderedByCustomer"];

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.labelContainer}>
                <label id={`${input.name}_label`} htmlFor={input.name}>
                    {input.description}
                </label>
                {input.tooltip && <HuskyTooltip input={input} />}
                {input.committed && <RevertButton input={input} onUncommit={onUncommit} />}
            </StyledWrapper>
            <Typography id={`${input.name}_tcInfoText`} as="p" {...styles.infoTextStyles}>
                {input.tcInfoText}
            </Typography>
            <Button
                {...styles.button}
                role="switch"
                aria-checked={truthyValues.includes(input.defaultValue.name)}
                aria-labelledby={`${input.name}_label`}
                aria-describedby={`${input.name}_tcInfoText`}
                id={input.name}
                onClick={handleClick}
            >
                {input.values.map(val => (
                    <ValueContainer key={val.name} selected={value === val.description}>
                        {value === val.description && <Icon {...styles.checkIconStyles} />}
                        <Typography {...styles.value}>{val.description}</Typography>
                    </ValueContainer>
                ))}
            </Button>
            <InputSubText input={input} />
        </StyledWrapper>
    );
};

export default ToggleField;
